/**
 * Where the module's screens are mounted.
 *
 * Every component below comes from `@inai/inventory-mui` unchanged. The shell supplies
 * only the things a router supplies: the `itemId` out of the path, a `onOpenItem` that
 * pushes a URL, a `onBack` that pops one, and a `onPosted` that goes somewhere sensible
 * after a movement is written.
 *
 * Explicit `<Route>` elements rather than a loop over `INVENTORY_ROUTES`, deliberately.
 * The manifest is a description — every entry's `component` is typed `ComponentType<any>`
 * because the screens take different props — so a loop would mount them all with no props
 * and no type checking. The manifest drives the *navigation*, which is what it is for;
 * mounting is written out so the compiler sees each screen's real signature.
 */

import { Alert, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useFeature } from '@inai/inventory-core/react';
import type { FeatureCode } from '@inai/inventory-core';
import {
  AvailabilityScreen,
  GoodsIssueScreen,
  GoodsReceiptScreen,
  ItemDetailScreen,
  ItemListScreen,
  StockEnquiryScreen,
} from '@inai/inventory-mui';

import { DiagnosticsScreen } from './DiagnosticsScreen';
import { NotFound } from './NotFound';
import { PlanScreen } from './PlanScreen';

/**
 * A screen reached by URL when the plan does not list its feature.
 *
 * The screen still renders. That is the honest thing: the shell has no authority to refuse
 * anything, the requests behind the screen will come back 403 naming the feature, and the
 * screens already render `FeatureNotInPlanError` on their error paths. Hiding the route
 * would dress a rendering hint up as a permission check, which is precisely what invariant
 * 7 says a hidden control is not.
 */
function PlanBanner({ feature }: { feature: FeatureCode }) {
  const { enabled, loading, suspended } = useFeature(feature);
  if (loading || enabled) return null;
  return (
    <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 13 }}>
        {suspended
          ? 'This organization is suspended, so every gated request below will be refused.'
          : `Your plan does not list ${feature.replace(/_/g, ' ')}. The screen still loads; the ` +
            'service will refuse the requests behind it with a 403 naming the feature.'}
      </Typography>
    </Alert>
  );
}

function Screen({ feature, children }: { feature?: FeatureCode; children: ReactElement }) {
  return (
    <>
      {feature ? <PlanBanner feature={feature} /> : null}
      {children}
    </>
  );
}

function ItemsRoute() {
  const navigate = useNavigate();
  return (
    <Screen feature="item_master">
      <ItemListScreen onOpenItem={(itemId) => navigate(`/items/${itemId}`)} />
    </Screen>
  );
}

function ItemDetailRoute() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  if (!itemId) return <Navigate to="/items" replace />;
  return (
    <Screen feature="item_master">
      <ItemDetailScreen itemId={itemId} onBack={() => navigate('/items')} />
    </Screen>
  );
}

function ReceiptRoute() {
  const navigate = useNavigate();
  return (
    <Screen feature="stock_ledger">
      <GoodsReceiptScreen onPosted={() => navigate('/stock')} />
    </Screen>
  );
}

function IssueRoute() {
  const navigate = useNavigate();
  return (
    <Screen feature="goods_issue">
      <GoodsIssueScreen onPosted={() => navigate('/stock')} />
    </Screen>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/items" replace />} />
      <Route path="/items" element={<ItemsRoute />} />
      <Route path="/items/:itemId" element={<ItemDetailRoute />} />
      <Route
        path="/stock"
        element={
          <Screen feature="stock_enquiry">
            <StockEnquiryScreen />
          </Screen>
        }
      />
      <Route
        path="/availability"
        element={
          <Screen feature="stock_reservation">
            <AvailabilityScreen />
          </Screen>
        }
      />
      <Route path="/receipts/new" element={<ReceiptRoute />} />
      <Route path="/issues/new" element={<IssueRoute />} />
      <Route path="/plan" element={<PlanScreen />} />
      <Route path="/diagnostics" element={<DiagnosticsScreen />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
